import React, { useMemo } from 'react';
import { Button, Descriptions, Drawer, Empty, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';

type Props = {
  resource: any | null;
  currency?: string;
  open: boolean;
  onClose: () => void;
};

const valueOrDash = (value: any) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const consoleProjectUrl = (projectId?: string) => projectId
  ? `https://console.cloud.google.com/home/dashboard?project=${encodeURIComponent(projectId)}`
  : undefined;

const billingReasonLabels: Record<string, string> = {
  'gke-ephemeral-node': 'Nó efêmero do GKE',
  'financial-commitment': 'Compromisso financeiro',
  'missing-from-current-inventory': 'Ausente no inventário atual'
};

const GcpResourceDetailDrawer: React.FC<Props> = ({ resource, currency = 'BRL', open, onClose }) => {
  const moneyFormatter = useMemo(() => new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency
  }), [currency]);
  const technicalEntries = Object.entries(resource?.technical || {});
  const labelEntries = Object.entries(resource?.labels || {});
  const dependencies = Array.isArray(resource?.dependencies) ? resource.dependencies : [];
  const skuBreakdown = Array.isArray(resource?.skuBreakdown) ? resource.skuBreakdown : [];
  const monthlyCostHistory = Array.isArray(resource?.monthlyCostHistory) ? resource.monthlyCostHistory : [];
  const projectUrl = consoleProjectUrl(resource?.projectId);

  return (
    <Drawer
      title={resource ? `Visão 360° — ${resource.name || 'Recurso GCP'}` : 'Visão 360° do recurso'}
      open={open}
      onClose={onClose}
      width="min(720px, 94vw)"
      destroyOnHidden
    >
      {!resource ? <Empty description="Selecione um recurso para ver os detalhes." /> : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Space wrap>
              <Tag color="blue">{resource.category || 'Recurso'}</Tag>
              <Tag>{resource.status || 'Monitorado'}</Tag>
              <Tag color={resource.costSource === 'billing-export' ? 'green' : 'default'}>
                {resource.costSource === 'billing-export' ? 'Billing Export' : resource.costSource || 'Inventário'}
              </Tag>
            </Space>
            <Typography.Paragraph copyable={{ text: resource.resourceId || resource.fullName }} style={{ marginTop: 12, marginBottom: 0 }}>
              {resource.resourceId || resource.fullName || 'Identificador indisponível'}
            </Typography.Paragraph>
            {resource.costAllocation === 'billing-only' && (
              <Typography.Paragraph type="warning" style={{ marginTop: 12, marginBottom: 0 }}>
                {resource.note || 'Este identificador existe no Billing Export, mas não foi encontrado no inventário técnico atual.'}
              </Typography.Paragraph>
            )}
          </div>

          <div className="gcp-inventory__totals">
            <Statistic
              title="Custo mensal atribuído"
              value={resource.monthlyCost || 0}
              formatter={value => resource.monthlyCost == null ? 'Sem custo direto' : moneyFormatter.format(Number(value))}
            />
            <Statistic
              title="Custo do período"
              value={resource.periodCost || 0}
              formatter={value => resource.periodCost == null ? '—' : moneyFormatter.format(Number(value))}
            />
            <Statistic title="Dependências conhecidas" value={dependencies.length || resource.dependencyCount || 0} />
          </div>

          <Descriptions title="Identidade e localização" bordered size="small" column={2}>
            <Descriptions.Item label="Projeto">{valueOrDash(resource.projectId)}</Descriptions.Item>
            <Descriptions.Item label="Provider">GCP</Descriptions.Item>
            <Descriptions.Item label="Serviço">{valueOrDash(resource.service)}</Descriptions.Item>
            <Descriptions.Item label="Tipo">{valueOrDash(resource.assetType || resource.type)}</Descriptions.Item>
            <Descriptions.Item label="Região/local">{valueOrDash(resource.location)}</Descriptions.Item>
            <Descriptions.Item label="Zona">{valueOrDash(resource.zone)}</Descriptions.Item>
            <Descriptions.Item label="Origem do inventário">{valueOrDash(resource.source)}</Descriptions.Item>
            <Descriptions.Item label="Coletado em">{valueOrDash(resource.collectedAt)}</Descriptions.Item>
            <Descriptions.Item label="Atribuição do custo" span={2}>{valueOrDash(resource.costAllocation)}</Descriptions.Item>
            <Descriptions.Item label="Serviço de faturamento" span={2}>{valueOrDash(resource.billingService)}</Descriptions.Item>
            {resource.billingOnlyReason && <Descriptions.Item label="Motivo financeiro">{valueOrDash(billingReasonLabels[resource.billingOnlyReason] || resource.billingOnlyReason)}</Descriptions.Item>}
            {resource.parentResourceName && <Descriptions.Item label="Recurso pai">{valueOrDash(resource.parentResourceName)}</Descriptions.Item>}
          </Descriptions>

          <div>
            <Typography.Title level={5}>Composição financeira por SKU</Typography.Title>
            {skuBreakdown.length ? (
              <Table
                size="small"
                pagination={false}
                rowKey={(row: any) => `${row.skuId}-${row.description}`}
                dataSource={skuBreakdown}
                columns={[
                  { title: 'SKU', dataIndex: 'description', key: 'description' },
                  { title: 'ID', dataIndex: 'skuId', key: 'skuId', render: valueOrDash },
                  { title: 'Média mensal fechada', dataIndex: 'monthlyCost', key: 'monthlyCost', align: 'right', render: (value: number) => moneyFormatter.format(Number(value || 0)) },
                  { title: 'Total do período', dataIndex: 'totalCost', key: 'totalCost', align: 'right', render: (value: number) => moneyFormatter.format(Number(value || 0)) }
                ]}
              />
            ) : <Typography.Text type="secondary">O Billing Export não informou SKU para este recurso.</Typography.Text>}
          </div>

          <div>
            <Typography.Title level={5}>Histórico mensal atribuído</Typography.Title>
            {monthlyCostHistory.length ? (
              <Table
                size="small"
                pagination={false}
                rowKey="month"
                dataSource={monthlyCostHistory}
                columns={[
                  { title: 'Mês', dataIndex: 'month', key: 'month' },
                  { title: 'Custo líquido', dataIndex: 'cost', key: 'cost', align: 'right', render: (value: number) => moneyFormatter.format(Number(value || 0)) }
                ]}
              />
            ) : <Typography.Text type="secondary">Não há histórico individual atribuído neste snapshot.</Typography.Text>}
          </div>

          <Descriptions title="Configuração técnica" bordered size="small" column={2}>
            {resource.vcpu != null && <Descriptions.Item label="vCPU">{resource.vcpu}</Descriptions.Item>}
            {resource.memoryGb != null && <Descriptions.Item label="Memória">{resource.memoryGb} GB</Descriptions.Item>}
            {resource.diskSizeGb != null && <Descriptions.Item label="Disco">{resource.diskSizeGb} GB</Descriptions.Item>}
            {resource.cpuAveragePercent != null && <Descriptions.Item label="CPU média">{Number(resource.cpuAveragePercent).toFixed(1)}%</Descriptions.Item>}
            {technicalEntries.map(([key, value]) => (
              <Descriptions.Item key={key} label={key} span={typeof value === 'object' ? 2 : 1}>
                {typeof value === 'object'
                  ? <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{valueOrDash(value)}</pre>
                  : valueOrDash(value)}
              </Descriptions.Item>
            ))}
            {!technicalEntries.length && resource.vcpu == null && resource.memoryGb == null && resource.diskSizeGb == null && (
              <Descriptions.Item label="Dados técnicos" span={2}>Não disponíveis neste snapshot.</Descriptions.Item>
            )}
          </Descriptions>

          <div>
            <Typography.Title level={5}>Labels</Typography.Title>
            {labelEntries.length ? (
              <Space wrap>
                {labelEntries.map(([key, value]) => <Tag key={key}>{key}={valueOrDash(value)}</Tag>)}
              </Space>
            ) : <Typography.Text type="secondary">Nenhuma label coletada.</Typography.Text>}
          </div>

          <div>
            <Typography.Title level={5}>Dependências conhecidas</Typography.Title>
            {dependencies.length ? dependencies.map((dependency: string) => (
              <Typography.Paragraph key={dependency} copyable={{ text: dependency }} style={{ marginBottom: 6 }}>
                {dependency}
              </Typography.Paragraph>
            )) : <Typography.Text type="secondary">Nenhuma dependência identificada no snapshot.</Typography.Text>}
          </div>

          {projectUrl && (
            <Button href={projectUrl} target="_blank" rel="noreferrer" icon={<ExportOutlined />}>
              Abrir projeto no Console GCP
            </Button>
          )}
        </Space>
      )}
    </Drawer>
  );
};

export default GcpResourceDetailDrawer;
